import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTimes, faArrowUpFromBracket } from '@fortawesome/free-solid-svg-icons';
import { Button, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import KnowledgeLimitInfoModal from './knowledgeLimitInfoModal.js';
import ConfirmImportModal from './ConfirmImportModal.js';
import { resolveSelection } from '../utils/resolveSelection.js';
import './GoogleDrivePickerModal.css';

import { splitTooLarge, describeTooLarge } from '../utils/knowledgeLimits.js';
import ManageConnectionsModal from './ManageConnectionsModal.js';
const API_URL = process.env.REACT_APP_API_BASE_URL;

// Colorful Google Drive SVG Logo Component
const GoogleDriveLogo = ({ size = 24, style = {} }) => (
  <svg width={size * 1.12} height={size} viewBox="0 0 87.3 78" style={style}>
    <path fill="#0066da" d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z"/>
    <path fill="#00ac47" d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z"/>
    <path fill="#ea4335" d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z"/>
    <path fill="#00832d" d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z"/>
    <path fill="#2684fc" d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z"/>
    <path fill="#ffba00" d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z"/>
  </svg>
);

function GoogleDrivePickerModal({ isOpen, onClose, onFilesSelected, botId, remainingSlots = Infinity, onUpgrade, returnTo = 'knowledge' }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  // 'Manage accounts' -> the shared ManageConnectionsModal (rename/delete a
  // connected account). Re-added 2026-08-18: the old Connect page that hosted it
  // is gone, and the picker is where accounts are chosen, so this is where
  // customers look for it.
  const [manageOpen, setManageOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [pickerLoaded, setPickerLoaded] = useState(false);
  const [importing, setImporting] = useState(false);
  const [limitModalOpen, setLimitModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [resolvedFiles, setResolvedFiles] = useState([]);
  const [resolveError, setResolveError] = useState(false);

  const oauthPopupRef = useRef(null);

  const isInIframe = () => {
    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  };

  // Load Google Picker API
  useEffect(() => {
    if (!isOpen) return;

    const loadGoogleApis = () => {
      // Check if gapi is already loaded
      if (window.gapi && window.google?.picker) {
        setPickerLoaded(true);
        return;
      }

      // Load the Google API script
      const script = document.createElement('script');
      script.src = 'https://apis.google.com/js/api.js';
      script.onload = () => {
        window.gapi.load('picker', () => {
          setPickerLoaded(true);
        });
      };
      document.body.appendChild(script);
    };

    loadGoogleApis();
  }, [isOpen]);

  // Fetch user's Google Drive connections
  const fetchConnections = useCallback(async () => {
    try {
      const userId = localStorage.getItem('user_id');
      const response = await fetch(`${API_URL}data-connections?user_id=${userId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const googleDriveConnections = (data.connections || []).filter(
          c => c.platform === 'google_drive' && c.status === 'active'
        );
        setConnections(googleDriveConnections);

        // Auto-select if only one connection
        if (googleDriveConnections.length === 1) {
          setSelectedConnection(googleDriveConnections[0]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch data connections:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      fetchConnections();
    }
  }, [isOpen, fetchConnections]);

  // Listen for files picked in a popup (iframe context)
  useEffect(() => {
    const handlePickerMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'gdrive-files-picked') {
        importFilesById(event.data.files);
      }
    };

    window.addEventListener('message', handlePickerMessage);
    return () => window.removeEventListener('message', handlePickerMessage);
  });

  const doImport = async (allFiles) => {
    // Per-file size cap (mirrors backend ingest_limits): refuse here, before a
    // row is created; native Docs/Sheets carry no size and are checked server-side.
    const { ok: files, tooLarge } = splitTooLarge(allFiles);
    if (tooLarge.length) alert(describeTooLarge(tooLarge, t));
    if (!files.length || !selectedConnection) return;
    setImporting(true);
    try {
      const response = await fetch(
        `${API_URL}data-connections/${selectedConnection.id}/import-files`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({ bot_id: botId, files }),
        }
      );

      if (!response.ok) {
        // 403s are handled globally by the permission-denied modal —
        // close this picker quietly instead of showing a generic alert.
        if (response.status === 403) {
          onClose();
          return;
        }
        throw new Error('Failed to import files');
      }

      const result = await response.json();
      if (onFilesSelected) {
        onFilesSelected(result.knowledge_ids, files);
      }
      onClose();
    } catch (error) {
      console.error('Error importing files:', error);
      alert('Failed to import files. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  // Import files using current selectedConnection
  const importFilesById = async (files) => {
    if (!files.length || !selectedConnection) return;

    // If a folder was picked, resolve its full contents on the backend and show
    // a confirmation list (with a plan-limit check) before importing.
    if (files.some(f => f.mimeType === 'application/vnd.google-apps.folder')) {
      setResolvedFiles([]);
      setResolveError(false);
      setConfirmOpen(true);
      setResolving(true);
      try {
        const resolved = await resolveSelection(selectedConnection.id, files);
        setResolvedFiles(resolved);
      } catch (error) {
        console.error('Error resolving folder selection:', error);
        setResolvedFiles([]);
        setResolveError(true);
      } finally {
        setResolving(false);
      }
      return;
    }

    // Single files only — enforce the limit and import directly.
    if (files.length > remainingSlots) {
      setLimitModalOpen(true);
      return;
    }
    await doImport(files);
  };

  const openPickerInPopup = (accessToken, appId) => {
    const width = 900;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    // Mirrors LISTABLE_MIME_TYPES in the backend's google_drive.py (minus the
    // folder type, which the picker handles via setIncludeFolders). Anything the
    // backend can extract but is missing here is simply invisible to the customer,
    // so the two lists have to move together.
    const mimeTypes = 'application/pdf,application/vnd.google-apps.document,application/vnd.google-apps.spreadsheet,text/plain,text/csv,text/markdown,application/rtf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword';
    const origin = window.location.origin;
    const appIdLine = appId ? `builder.setAppId('${appId}');` : '';
    const popupTitle = t('gdrive_popup_title');
    const popupLoading = t('gdrive_popup_loading');
    const pickerTitle = t('select_files');

    const popup = window.open('', 'gdrive-picker-popup',
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
    );

    popup.document.write(`<!DOCTYPE html>
<html><head><title>${popupTitle}</title></head>
<body style="margin:0;display:flex;justify-content:center;align-items:center;height:100vh;font-family:sans-serif;background:#fafafa;">
<p style="color:#666;">${popupLoading}</p>
<script>
var s = document.createElement('script');
s.src = 'https://apis.google.com/js/api.js';
s.onload = function() {
  gapi.load('picker', function() {
    var view = new google.picker.DocsView()
      .setIncludeFolders(true)
      .setSelectFolderEnabled(false)
      .setMimeTypes('${mimeTypes}');
    var builder = new google.picker.PickerBuilder()
      .addView(view)
      .setOAuthToken('${accessToken}')
      .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
      .setTitle('${pickerTitle}')
      .setCallback(function(data) {
        if (data.action === google.picker.Action.PICKED) {
          var files = data.docs.map(function(doc) {
            return { id: doc.id, name: doc.name, mimeType: doc.mimeType, url: doc.url, sizeBytes: doc.sizeBytes };
          });
          window.opener.postMessage({ type: 'gdrive-files-picked', files: files }, '${origin}');
          window.close();
        } else if (data.action === google.picker.Action.CANCEL) {
          window.close();
        }
      });
    ${appIdLine}
    builder.build().setVisible(true);
  });
};
document.body.appendChild(s);
</script>
</body></html>`);
    popup.document.close();
  };

  const openPicker = async () => {
    if (!selectedConnection || (!pickerLoaded && !isInIframe())) return;

    try {
      // Get fresh access token for Picker
      const tokenResponse = await fetch(
        `${API_URL}data-connections/${selectedConnection.id}/picker-token`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      if (!tokenResponse.ok) {
        throw new Error('Failed to get picker token');
      }

      const { access_token, app_id } = await tokenResponse.json();

      // When in an iframe, open the picker in a popup to avoid origin mismatch
      if (isInIframe()) {
        openPickerInPopup(access_token, app_id);
        return;
      }

      // Create and show the picker
      const view = new window.google.picker.DocsView()
        .setIncludeFolders(true)
        .setSelectFolderEnabled(false)
        .setMimeTypes(
          'application/pdf,' +
          'application/vnd.google-apps.document,' +
          'application/vnd.google-apps.spreadsheet,' +
          'text/plain,' +
          'text/csv,' +
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document,' +
          'application/msword'
        );

      // Build picker - setAppId is REQUIRED for drive.file scope to grant file access
      const pickerBuilder = new window.google.picker.PickerBuilder()
        .addView(view)
        .setOAuthToken(access_token)
        .setCallback(handlePickerCallback)
        .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
        .setTitle(t('select_files') || 'Select files from Google Drive');

      // App ID (Google Cloud Project Number) is required for drive.file scope
      if (app_id) {
        pickerBuilder.setAppId(app_id);
      }

      const picker = pickerBuilder.build();

      picker.setVisible(true);
    } catch (error) {
      console.error('Error opening picker:', error);
      alert('Failed to open Google Drive picker. Please try again.');
    }
  };

  const handlePickerCallback = async (data) => {
    if (data.action === window.google.picker.Action.PICKED) {
      const files = data.docs.map(doc => ({
        id: doc.id,
        name: doc.name,
        mimeType: doc.mimeType,
        url: doc.url,
      }));

      await importFilesById(files);
    }
  };

  const importFiles = async (files) => {
    await importFilesById(files);
  };

  const handleConnectGoogleDrive = async () => {
    try {
      const userId = localStorage.getItem('user_id');
      const response = await fetch(
        `${API_URL}data-connections/google-drive/connect?user_id=${userId}&return_to=${encodeURIComponent(returnTo)}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('pendingKnowledgeTab', 'googledrive');
        localStorage.setItem('pendingOpenPicker', 'googledrive');

        if (isInIframe()) {
          const width = 600;
          const height = 700;
          const left = window.screenX + (window.outerWidth - width) / 2;
          const top = window.screenY + (window.outerHeight - height) / 2;

          oauthPopupRef.current = window.open(
            data.authorization_url,
            'google-drive-oauth',
            `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
          );

          const checkPopup = setInterval(() => {
            if (oauthPopupRef.current?.closed) {
              clearInterval(checkPopup);
              oauthPopupRef.current = null;
              fetchConnections();
            }
          }, 1000);
        } else {
          window.location.href = data.authorization_url;
        }
      }
    } catch (error) {
      console.error('Failed to connect Google Drive:', error);
      alert('Failed to connect Google Drive');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="gdrive-modal-overlay" onClick={onClose}>
      <div className="gdrive-modal-content" onClick={e => e.stopPropagation()}>
        <div className="gdrive-modal-header">
          <h2>
            <GoogleDriveLogo size={24} style={{ marginRight: '10px' }} />
            {t('gdrive_import_title')}
          </h2>
          <button className="gdrive-modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="gdrive-modal-body">
          {loading ? (
            <div className="gdrive-loading">
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              <p>{t('loading')}</p>
            </div>
          ) : connections.length === 0 ? (
            <div className="gdrive-no-connections">
              <GoogleDriveLogo size={64} style={{ marginBottom: '0px', opacity: 0.5 }} />
              <p>{t('gdrive_no_account_connected')}</p>
              <p className="gdrive-hint">
                {t('gdrive_connect_hint')}
              </p>
              <Button
                onClick={handleConnectGoogleDrive}
                variant="contained"
                sx={{
                  backgroundColor: '#5616ea',
                  '&:hover': { backgroundColor: '#4a14d6' },
                }}
              >
                {t('connect_google_drive')}
              </Button>
            </div>
          ) : (
            <>
              <div className="gdrive-account-selector">
                <FormControl
                  fullWidth
                  sx={{
                    '& label.Mui-focused': {
                      color: '#5616ea',
                    },
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        border: "1px solid #515151",
                      },
                    },
                  }}
                >
                  <InputLabel id="gdrive-account-select-label">{t('gdrive_select_account')}</InputLabel>
                  <Select
                    labelId="gdrive-account-select-label"
                    value={selectedConnection?.id || ''}
                    onChange={e => {
                      const conn = connections.find(c => c.id === parseInt(e.target.value));
                      setSelectedConnection(conn);
                    }}
                    label={t('gdrive_select_account')}
                  >
                    {connections.map(conn => (
                      <MenuItem key={conn.id} value={conn.id}>
                        {conn.display_name || conn.external_id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <span
                  className="gdrive-add-account-link"
                  onClick={handleConnectGoogleDrive}
                >
                  {t('add_another_account')}
                </span>
                {connections.length > 0 && (

                  <span className="gdrive-add-account-link" style={{ marginLeft: '16px' }} onClick={() => setManageOpen(true)}>

                    {t('manage_accounts', 'Manage accounts')}

                  </span>

                )}
              </div>

              <div className="gdrive-picker-section">
                <p className="gdrive-instructions">
                  {t('gdrive_picker_instructions')}
                </p>

                <div className="gdrive-file-tag-parent">
                  <div className="gdrive-file-tag">.pdf</div>
                  <div className="gdrive-file-tag">.txt</div>
                  <div className="gdrive-file-tag">.doc</div>
                  <div className="gdrive-file-tag">.docx</div>
                  <div className="gdrive-file-tag">.csv</div>
                </div>

                <Button
                  onClick={openPicker}
                  variant="contained"
                  disabled={!selectedConnection || (!pickerLoaded && !isInIframe()) || importing}
                  sx={{
                    backgroundColor: '#5616ea',
                    '&:hover': { backgroundColor: '#4a14d6' },
                    '&.Mui-disabled': {
                      backgroundColor: '#e0e0e0',
                      color: '#9e9e9e',
                    },
                  }}
                  fullWidth
                >
                  {importing ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '8px' }} />
                      {t('importing')}
                    </>
                  ) : (!pickerLoaded && !isInIframe()) ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '8px' }} />
                      {t('loading')}
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faArrowUpFromBracket} style={{ marginRight: '8px' }} />
                      {t('gdrive_select_files')}
                    </>
                  )}
                </Button>
              </div>

              <div className="gdrive-modal-actions">
                <Button
                  onClick={onClose}
                  variant="outlined"
                  sx={{
                    color: '#5616ea',
                    borderColor: '#5616ea',
                    '&:hover': {
                      borderColor: '#5e0fb3',
                      backgroundColor: '#f3eaff',
                    },
                  }}
                >
                  {t('cancel')}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      <KnowledgeLimitInfoModal
        open={limitModalOpen}
        onClose={() => setLimitModalOpen(false)}
        onUpgrade={() => { setLimitModalOpen(false); onUpgrade && onUpgrade(); }}
        remainingSlots={remainingSlots}
      />

      <ConfirmImportModal
        open={confirmOpen}
        loading={resolving}
        error={resolveError}
        files={resolvedFiles}
        importing={importing}
        remainingSlots={remainingSlots}
        onClose={() => setConfirmOpen(false)}
        onConfirm={(selected) => { setConfirmOpen(false); doImport(selected); }}
        onUpgrade={() => { setConfirmOpen(false); onUpgrade && onUpgrade(); }}
      />
      {/* Portal-rendered, but a React child of the overlay above (onClick=onClose):
          clicks inside the manage/confirm dialogs would bubble to it and close the
          picker before the delete could run — so stop them here. */}
      <div onClick={(e) => e.stopPropagation()}>
        <ManageConnectionsModal
          open={manageOpen}
          onClose={() => setManageOpen(false)}
          connections={connections}
          onUpdate={fetchConnections}
          platform="google_drive"
          connectionType="data"
        />
      </div>
    </div>
  );
}

export default GoogleDrivePickerModal;
